/**
 * HANNE BLOCH, two complimentary pins with every Knit Belted Top (2026-10-12, Vic: "When a Belted Top is in cart, then
 * it should show 2 pins in the 'gift' field for the customer to add. (pre-select the matching Gold or Silver color, but
 * allow them to change if they prefer)"; "Go ahead and build this").
 *
 * Loaded by snippets/hb-pins (the drawer on every page, and the cart page) for consumers only, while Theme settings >
 * Pins with a belted top > "Turn on for customers" is on or the cart is a ?hb_gift=preview cart. The module is Liquid
 * (snippets/hb-cart-pins, in the drawer's gift field; its place there is assets/hb-cart.js showPins). This script:
 *   - the module: a tap on Gold or Silver chooses (the metal of her newest belted top comes chosen); ADD MY GIFT puts
 *     two pins per belted top in the cart, one line with the property _hb_gift = pins. In an open cart the module
 *     folds away and the pins float up into the list under her top, "Added to your cart" for 3 s;
 *   - after every other cart change, the pins follow her tops: two per belted top (a second top adds two of its own
 *     metal to the pins she has, a top that leaves takes two with it, a metal no top of hers has going first), and
 *     when no belted top is left the pins leave too, or they would be charged. If her top comes back within moments
 *     (Undo), its pins come back with it. Pins she removes herself stay out (the module offers them again);
 *   - the line's "Color: Gold" swaps Gold and Silver in place (assets/hb-cart.js, the mini bag's way).
 * Safety: for customers (not a preview cart) the pins must be at 0. Pins that are not (no discount applies to them in
 * this cart) are taken straight back out and the feature rests for the visit (sessionStorage hb-pins-off).
 * Every request asks for the drawer's section (and the mini bag's state, when that feature is on the page) and hands
 * the drawer to the theme through one CartUpdateEvent (source hb-pins), without opening a closed cart. Klaviyo's
 * "Added to Cart" is not sent for the pins: a gift is not product interest.
 */
import { CartUpdateEvent } from '@theme/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const GIFT_SECTION = 'hb-tgift-state';
const OFF_KEY = 'hb-pins-off';
const WAS_KEY = 'hb-pins-was'; // { metal, at }: the pins taken out with her last top, brought back if the top returns
const RESTORE_MS = 15000;
const FOLD_MS = 350;
const ARRIVE_MS = 600;
const ADDED_MS = 3000;
const CHECK_SVG =
  '<svg width="11" height="11" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4.75 10.75l2.93 3.84c.1.14.16.21.22.23a.2.2 0 0 0 .18 0c.07-.02.12-.1.23-.23l6.93-8.85" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

let DATA = {};
try {
  DATA = JSON.parse(document.getElementById('hb-pins-data')?.textContent || '{}');
} catch (e) {
  DATA = {};
}
const GOLD = new Set((DATA.gold || []).map(String));
const SILVER = new Set((DATA.silver || []).map(String));
const VID = { gold: String(DATA.goldVid || ''), silver: String(DATA.silverVid || '') };
const METAL_OF = new Map([
  [VID.gold, 'gold'],
  [VID.silver, 'silver'],
]);

function recall(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function remember(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    /* no storage: this page only */
  }
}
function forget(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    /* nothing to forget */
  }
}

const S = {
  busy: false,
  again: false,
  timer: 0,
  fails: 0,
  sel: null, // the metal she tapped in the module
  had: null, // pins in the cart at the last look
  own: false, // our own request on its way
  pendingArrive: null, // { key, note }: the pins' line floats in once the drawer shows it
  off: recall(OFF_KEY) === '1',
};
if (S.off) document.documentElement.classList.add('hb-pins-off');

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const drawer = () => document.querySelector('cart-drawer-component');
const dialogEl = () => document.querySelector('cart-drawer-component dialog');
const drawerOpen = () => !!(dialogEl() && dialogEl().open);
const sectionId = () => document.querySelector('cart-drawer-component cart-items-component')?.dataset.sectionId;
// HB 2026-10-12 (the cart page in line with the drawer): the cart page has no drawer; its summary carries the module.
const modEl = () =>
  document.querySelector('cart-drawer-component [data-hb-pins-mod]') || document.querySelector('[data-hb-cpage] [data-hb-pins-mod]');
const giftOnPage = () => !!document.getElementById('hb-tg-data');

/* ---------- cart requests ---------- */

async function post(path, body) {
  const sid = sectionId();
  const payload = Object.assign({}, body);
  if (sid) {
    payload.sections = giftOnPage() ? `${sid},${GIFT_SECTION}` : sid;
    payload.sections_url = window.location.pathname;
  }
  const res = await fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const err = new Error(data.description || data.message || `${path} failed`);
    err.data = data;
    throw err;
  }
  return data;
}

async function getCart() {
  const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!res.ok) throw new Error('cart.js failed');
  return res.json();
}

function countFrom(data) {
  const html = data && data.sections && data.sections[sectionId()];
  if (!html) return undefined;
  const n = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
  return n != null && n.trim() !== '' ? parseInt(n, 10) || 0 : 0;
}

// Vic's rule from the cart (assets/hb-cart.js): while the cart is open, changes made inside it never change its height.
function holdOpenCart() {
  const dlgEl = dialogEl();
  if (!dlgEl || !dlgEl.open) return;
  const hb = window.HBCart && window.HBCart.state;
  if (hb && !hb.keepMode) hb.keepMode = dlgEl.classList.contains('hb-cart--full') ? 'full' : 'sheet';
  const root = document.documentElement;
  if (!window.matchMedia('(max-width: 749px)').matches || root.classList.contains('hb-cart-held')) return;
  if (!dlgEl.classList.contains('hb-cart--sheet')) return;
  root.style.setProperty('--hb-cart-h', `${Math.round(dlgEl.getBoundingClientRect().height)}px`);
  root.classList.add('hb-cart-held');
}

// Hands the new drawer to the theme (the cart page's own list fetches itself). A closed cart stays closed: its
// auto-open is lifted for this one event, as the quick add and the mini bag do.
function publish(data) {
  const root = drawer();
  const open = drawerOpen();
  if (open) holdOpenCart();
  const lift = !open && root && root.hasAttribute('auto-open');
  const value = lift ? root.getAttribute('auto-open') : null;
  if (lift) root.removeAttribute('auto-open');
  const detail = { source: 'hb-pins' };
  if (data && data.sections && sectionId() && data.sections[sectionId()]) {
    detail.sections = data.sections;
    detail.itemCount = countFrom(data);
  }
  try {
    document.dispatchEvent(new CartUpdateEvent({}, 'hb-pins', detail));
  } finally {
    if (lift) setTimeout(() => root.setAttribute('auto-open', value || ''), 0);
  }
}

// The cart's other scripts finish their requests first (assets/hb-cart.js Remove and Undo, assets/hb-tgift.js the bag),
// so two answers never cross; at most 3 s.
function settle() {
  return new Promise((resolve) => {
    const start = Date.now();
    const look = () => {
      const c = window.HBCart && window.HBCart.state;
      const g = window.HBGift && window.HBGift.state;
      const busy = !!((c && (c.busy || c.pending > 0)) || (g && g.busy));
      if (!busy || Date.now() - start > 3000) resolve();
      else setTimeout(look, 120);
    };
    look();
  });
}

/* ---------- what is in the cart ---------- */

function count(cart) {
  const c = { gold: 0, silver: 0, tops: 0, newest: '', newestKey: '', pins: [], P: 0, preview: false };
  c.preview = !!(cart && cart.attributes && cart.attributes._hb_tgift_preview === '1');
  // Shopify lists the newest line first: the first top met is the one she added last.
  ((cart && cart.items) || []).forEach((it) => {
    const props = it.properties || {};
    if (props._hb_gift === 'pins') {
      const vid = String(it.variant_id);
      c.pins.push({ key: it.key, vid, qty: it.quantity, metal: METAL_OF.get(vid) || '', final: Number(it.final_line_price) || 0 });
      c.P += it.quantity;
      return;
    }
    const pid = String(it.product_id);
    const metal = GOLD.has(pid) ? 'gold' : SILVER.has(pid) ? 'silver' : '';
    if (!metal) return;
    c[metal] += it.quantity;
    c.tops += it.quantity;
    if (!c.newest) {
      c.newest = metal;
      c.newestKey = it.key;
    }
  });
  return c;
}

// Safety for customers: the pins come in at 0 or not at all.
function charged(c) {
  return !!(DATA.live && !c.preview && c.pins.some((p) => p.final > 0));
}

async function rest(c) {
  console.warn('[HB pins] the pins are not complimentary in this cart (is a discount live for these tops?). Taken out again.');
  S.off = true;
  remember(OFF_KEY, '1');
  document.documentElement.classList.add('hb-pins-off');
  const updates = {};
  c.pins.forEach((p) => {
    updates[p.key] = 0;
  });
  const data = await post('cart/update.js', { updates });
  S.had = 0;
  publish(data);
}

/* ---------- actions ---------- */

async function addPins(metal, qty, opts = {}) {
  const vid = VID[metal] || VID.gold || VID.silver;
  if (!vid || qty < 1 || S.off) return;
  S.own = true;
  try {
    const data = await post('cart/add.js', { items: [{ id: Number(vid), quantity: qty, properties: { _hb_gift: 'pins' } }] });
    const key = data && data.items && data.items[0] && data.items[0].key;
    const c = count(await getCart());
    if (charged(c)) {
      await rest(c);
      return;
    }
    S.had = c.P;
    // The pins take their place right under her newest belted top (assets/hb-cart.js keeps the drawer's line order).
    const hb = window.HBCart && window.HBCart.state;
    if (key && hb && Array.isArray(hb.order) && !hb.order.includes(key)) {
      const at = c.newestKey ? hb.order.indexOf(c.newestKey) : -1;
      hb.order.splice(at === -1 ? hb.order.length : at + 1, 0, key);
    }
    S.pendingArrive = key ? { key, note: !opts.quiet } : null;
    S.sel = null;
    publish(data);
  } finally {
    S.own = false;
  }
}

// Quantities by line key in one request; lines set to 0 fold away first in an open cart.
async function setQty(updates) {
  const keys = Object.keys(updates);
  if (!keys.length) return;
  const going = keys.filter((k) => updates[k] === 0);
  let folded = false;
  if (drawerOpen()) {
    going.forEach((k) => {
      const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(k)}"]`);
      if (!line) return;
      line.style.maxHeight = `${line.offsetHeight}px`;
      void line.offsetHeight;
      line.classList.add('hb-pins-going');
      folded = true;
    });
  }
  S.own = true;
  try {
    const [data] = await Promise.all([post('cart/update.js', { updates }), wait(folded ? FOLD_MS : 0)]);
    const c = count({ items: data.items || [], attributes: data.attributes || {} });
    S.had = c.P;
    publish(data);
    return c;
  } finally {
    S.own = false;
  }
}

// After every cart change that is not ours: two pins per belted top.
async function follow(cart) {
  const c = count(cart);
  const had = S.had;
  S.had = c.P;
  if (!c.P) {
    if (!c.tops || S.off) return;
    let was = null;
    try {
      was = JSON.parse(recall(WAS_KEY) || 'null');
    } catch (e) {
      was = null;
    }
    if (was && Date.now() - (Number(was.at) || 0) < RESTORE_MS) {
      // her top is back (Undo) moments after its pins left with it: they come back too
      forget(WAS_KEY);
      await addPins(was.metal || c.newest || 'gold', c.tops * 2, { quiet: true });
      return;
    }
    // she took the pins out herself: they stay out, the module offers them again
    if (had) forget(WAS_KEY);
    return;
  }
  if (!c.tops) {
    // her last belted top has gone: the pins go with it (they would be charged), and come back if it does
    remember(WAS_KEY, JSON.stringify({ metal: c.pins[0].metal || 'gold', at: Date.now() }));
    const updates = {};
    c.pins.forEach((p) => {
      updates[p.key] = 0;
    });
    await setQty(updates);
    return;
  }
  const want = c.tops * 2;
  if (c.P === want) {
    if (charged(c)) await rest(c);
    return;
  }
  const updates = {};
  if (c.P < want) {
    // another top: two more pins, on the line of that top's metal if she has one, else on her pins' line
    const line = c.pins.find((p) => p.metal === c.newest) || c.pins[0];
    updates[line.key] = line.qty + (want - c.P);
  } else {
    // a top fewer: the pins of a metal none of her tops has go first, then the others
    let extra = c.P - want;
    const order = c.pins.slice().sort((a, b) => (c[a.metal] || 0) - (c[b.metal] || 0));
    order.forEach((p) => {
      if (!extra) return;
      const take = Math.min(extra, p.qty);
      updates[p.key] = p.qty - take;
      extra -= take;
    });
  }
  // In step first, then the safety look: a pin over two per top is charged until its quantity follows the tops.
  const after = await setQty(updates);
  if (after && after.P && charged(after)) await rest(after);
}

async function check() {
  if (S.busy) {
    S.again = true;
    return;
  }
  S.busy = true;
  try {
    await settle();
    await follow(await getCart());
    S.fails = 0;
  } catch (err) {
    console.warn('[HB pins]', err);
    S.fails += 1;
  } finally {
    S.busy = false;
    if (S.again && S.fails < 3) {
      S.again = false;
      schedule(60);
    }
  }
}

function schedule(ms = 200) {
  clearTimeout(S.timer);
  S.timer = setTimeout(check, ms);
}

/* ---------- the module ---------- */

function inner() {
  const m = modEl();
  return m ? m.querySelector('[data-hb-pins-in]') : null;
}

// The metal she tapped stays chosen through the drawer's re-renders.
function paint() {
  const el = inner();
  if (!el) {
    S.sel = null;
    return;
  }
  const metal = S.sel || el.dataset.sel || 'gold';
  el.querySelectorAll('.hb-pins-sw').forEach((b) => {
    const on = b.dataset.metal === metal;
    if (b.classList.contains('is-sel') !== on) b.classList.toggle('is-sel', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  el.querySelectorAll('.hb-pins__pic').forEach((p) => p.classList.toggle('is-on', p.dataset.metal === metal));
  const name = el.querySelector('[data-hb-pins-name]');
  const nameText = el.dataset[`${metal}Name`];
  if (name && nameText && name.textContent !== nameText) name.textContent = nameText;
  const price = el.querySelector('[data-hb-pins-price]');
  const priceText = el.dataset[`${metal}Price`];
  if (price && priceText && price.textContent !== priceText) price.textContent = priceText;
}

async function addFromModule() {
  const m = modEl();
  const el = inner();
  if (!m || !el || m.classList.contains('is-busy')) return;
  const metal = S.sel || el.dataset.sel || 'gold';
  const qty = Number(el.dataset.qty) || 2;
  m.classList.add('is-busy');
  // a look at the cart already on its way finishes first
  for (let i = 0; S.busy && i < 25; i += 1) await wait(120);
  S.busy = true;
  const fold = drawerOpen();
  holdOpenCart();
  if (fold) m.classList.add('is-adding');
  try {
    await Promise.all([addPins(metal, qty), wait(fold ? FOLD_MS : 0)]);
  } catch (err) {
    console.warn('[HB pins]', err);
    m.classList.remove('is-adding');
  } finally {
    S.busy = false;
    m.classList.remove('is-busy');
    if (S.again) {
      S.again = false;
      schedule(60);
    }
  }
}

/* ---------- the pins' line: it floats in ---------- */

function arrive() {
  const p = S.pendingArrive;
  if (!p || !drawerOpen()) return;
  const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(p.key)}"]`);
  if (!line) return;
  S.pendingArrive = null;
  line.classList.add('hb-pins-arriving');
  setTimeout(() => line.isConnected && line.classList.remove('hb-pins-arriving'), ARRIVE_MS + 60);
  // a held sheet (assets/hb-cart.js) grows with the new line, so it never arrives below the fold of the lines
  document.dispatchEvent(new CustomEvent('hb:tg-arrive', { detail: { key: p.key } }));
  if (!p.note) return;
  const acts = line.querySelector('.hb-line__acts');
  if (!acts || acts.querySelector('.hb-line__added')) return;
  const note = document.createElement('span');
  note.className = 'hb-line__added';
  note.setAttribute('role', 'status');
  note.innerHTML = `${CHECK_SVG}<span>Added to your cart</span>`;
  acts.prepend(note);
  setTimeout(() => {
    note.classList.add('is-gone');
    setTimeout(() => note.remove(), 420);
  }, ADDED_MS);
}

/* ---------- wiring ---------- */

let scheduled = false;
function afterMorph() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    paint();
    arrive();
  });
}

function onClick(event) {
  const t = event.target instanceof Element ? event.target : null;
  if (!t) return;
  const sw = t.closest('[data-hb-pins-mod] .hb-pins-sw');
  if (sw) {
    event.preventDefault();
    if (!sw.disabled) {
      S.sel = sw.dataset.metal || null;
      paint();
    }
    return;
  }
  if (t.closest('[data-hb-pins-mod] [data-hb-pins-add]')) {
    event.preventDefault();
    addFromModule();
  }
}

function onCartUpdate(event) {
  const d = event.detail || {};
  const source = (d.data && d.data.source) || d.sourceId;
  if (source === 'hb-pins' || (d.data && d.data.didError)) return;
  S.fails = 0;
  schedule(200);
}

function init() {
  if (!VID.gold && !VID.silver) return;
  document.addEventListener('click', onClick);
  document.addEventListener('cart:update', onCartUpdate);
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) schedule(0);
  });
  const root = drawer();
  if (root) {
    new MutationObserver(afterMorph).observe(root, { childList: true, subtree: true });
    let wasOpen = drawerOpen();
    new MutationObserver(() => {
      const open = drawerOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      if (open) setTimeout(arrive, 350); // after the drawer has slid in
    }).observe(root, { attributes: true, attributeFilter: ['open'], subtree: true });
  }
  paint();
  // Pins already in the cart from an earlier page (or the cart page itself): one look that they still follow her tops.
  if (DATA.cartPage || document.querySelector('cart-drawer-component .hb-line--pins')) schedule(800);
  window.HBPins = { version: '2026-10-12b', state: S, check };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
